import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, HelpCircle, Lightbulb } from 'lucide-react';
import { Badge } from '../UI';
import { dogInitial } from '../../utils/dogAvatar';
import { type DnaSubjectSummary, type ParentageVerdict, hasDnaData, subjectPath } from './dnaTypes';

export const useSexLabel = () => {
  const { t } = useTranslation();
  return (sex: string | null | undefined) => t(`dogs:sex.${(sex ?? 'unknown').toLowerCase()}`);
};

/**
 * Erklaerung zu einem Schritt oder Bereich: kurz sagen, was passiert und warum - die Hauptidee des Assistenten.
 * Mit `details` laesst sich ein laengerer Text aufklappen, damit die Seite nicht ueberladen wirkt.
 */
export const ExplainBox: React.FC<{
  title?: string;
  children: React.ReactNode;
  details?: React.ReactNode;
  tone?: 'info' | 'tip';
}> = ({ title, children, details, tone = 'info' }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const Icon = tone === 'tip' ? Lightbulb : HelpCircle;
  return (
    <div className="rounded-xl border border-secondary-500/25 bg-secondary-500/10 p-4 text-sm text-dark-200">
      <div className="flex items-start gap-3">
        <Icon size={18} className="mt-0.5 shrink-0 text-secondary-300" aria-hidden="true" />
        <div className="min-w-0 flex-1 space-y-2">
          {title && <p className="font-semibold text-dark-50">{title}</p>}
          <div className="space-y-2 leading-relaxed">{children}</div>
          {details && (
            <>
              <button
                type="button"
                className="flex items-center gap-1 text-xs font-medium text-secondary-300 hover:text-secondary-200"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
              >
                {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                {open ? t('dna:explain.less') : t('dna:explain.more')}
              </button>
              {open && <div className="space-y-2 leading-relaxed text-dark-300">{details}</div>}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const VERDICT_VARIANT: Record<ParentageVerdict, 'success' | 'secondary' | 'warning' | 'error'> = {
  Compatible: 'success',
  Doubtful: 'warning',
  Excluded: 'error',
  InsufficientMarkers: 'secondary',
  NotComparable: 'secondary',
  NoProfile: 'secondary'
};

/** Kennzahlen eines Hundes bzw. einer Probe als kleine Badges: Marker, Rohdaten, Farbgene, Gentests, COI. */
export const SubjectBadges: React.FC<{ subject: DnaSubjectSummary }> = ({ subject }) => {
  const { t } = useTranslation();
  const markers = subject.snpMarkers > 0 ? `SNP ${subject.snpMarkers}` : subject.strMarkers > 0 ? `STR ${subject.strMarkers}` : null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {markers && <Badge variant="primary" size="sm" title={t('dna:badges.profileTitle')}>{markers}</Badge>}
      {subject.hasRawData && <Badge variant="success" size="sm" title={t('dna:badges.rawTitle')}>{t('dna:badges.raw')}</Badge>}
      {subject.hasTraits && <Badge variant="secondary" size="sm">{t('dna:badges.traits')}</Badge>}
      {subject.geneticTests > 0 && <Badge variant="secondary" size="sm">{t('dna:badges.genetic', { count: subject.geneticTests })}</Badge>}
      {subject.coiPercent != null && <Badge variant="secondary" size="sm">{t('dna:badges.coi', { value: subject.coiPercent })}</Badge>}
      {!hasDnaData(subject) && <Badge variant="secondary" size="sm">{t('dna:badges.noData')}</Badge>}
    </div>
  );
};

/** Karte eines Hundes bzw. einer Probe in der Uebersicht - fuehrt zur Detailseite. */
export const SubjectCard: React.FC<{ subject: DnaSubjectSummary }> = ({ subject }) => {
  const { t } = useTranslation();
  const sexLabel = useSexLabel();
  return (
    <Link
      to={subjectPath(subject)}
      className="card card-hover flex min-w-0 items-start gap-3 !p-4 text-left"
    >
      <span className="avatar-badge h-11 w-11 shrink-0 overflow-hidden text-base">
        {subject.photoUrl ? (
          <img src={subject.photoUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          dogInitial(subject.name)
        )}
      </span>
      <span className="min-w-0 flex-1 space-y-1.5">
        <span className="block truncate font-semibold text-dark-50">{subject.name}</span>
        <span className="block truncate text-xs text-dark-400">
          {[sexLabel(subject.sex), subject.breedName, subject.type === 'Sample' ? t('dna:subject.sampleHint') : null].filter(Boolean).join(' · ')}
        </span>
        <SubjectBadges subject={subject} />
      </span>
    </Link>
  );
};
